import { inject } from 'mobx-react'
import * as React from 'react'
import { Helmet } from 'react-helmet'
import ResponsiveAppBar from '../common/ResponsiveAppBar'
import { IStores } from '../Stores'
import {
  LocalAtm,
  Check,
  CreditCard,
  Delete,
  Drafts,
  UndoRounded,
} from '@mui/icons-material'
import {
  Typography,
  TableCell,
  Table,
  TableRow,
  Button,
  CircularProgress,
  FormControl,
  TableBody,
  TableHead,
  TextField,
  Grid,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Paper,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { formatCurrency, formatDate } from 'src/utils/Formatter'
import { pagedList } from 'src/viewModels/pagedList'
import ReactPaginate from 'react-paginate'
import moment from 'moment'
import { ReceivedCheckSearchRequest } from 'src/viewModels/ReceivedCheckSearchRequest'
import { ReceivedCheckSummaryResult } from 'src/viewModels/ReceivedCheckSummaryResult'
import {
  getReceivedChecksTotalsSummary,
  searchReceivedChecks,
} from 'src/services/SearchService'
import { ReceivedCheckTotalSummary } from 'src/viewModels/ReceivedCheckTotalSummary'
import UnApplyCheckModal from './UnApplyCheckModal'
import { ArchiveOutline } from 'mdi-material-ui'
import {
  getUnappliedReceivedCheckHistory,
  getReceivedCheckAppliedFundsDetail,
} from 'src/services/CheckService'
import { ReceivedCheckDetailSummary } from 'src/viewModels/ReceivedCheckDetailSummary'
import CheckDetailsModal from './CheckDetailsModal'
import MarkAsDeletedModal from './MarkAsDeletedModal'
import RefundedCheckDetailsModal from './RefundedCheckDetailsModal'

const CurrencyFormat: any = require('react-currency-format')

interface IReceivedCheckProps {
  setCheckRequest?: (request: ReceivedCheckSearchRequest) => void
  storedRequest?: ReceivedCheckSearchRequest
}

class ReceivedChecks extends React.Component<IReceivedCheckProps> {
  public state = {
    searchPayer: this.props.storedRequest?.payer ?? '',
    searchCheckNumber: this.props.storedRequest?.checkNumber ?? '',
    searchCheckDateStart: this.props.storedRequest?.checkDateStart ?? undefined,
    searchCheckDateEnd: this.props.storedRequest?.checkDateEnd ?? undefined,
    searchShowRefundOnly: this.props.storedRequest?.onlyShowRefunds ?? false,
    searchEocOrLegacy: this.props.storedRequest?.episodeOfCareNumberOrLegacyId ?? '',
    searchClaimGroupNumber: this.props.storedRequest?.claimGroupNumber ?? undefined,
    searchUnappliedFunds: this.props.storedRequest?.hasUnappliedFunds ?? false,
    searchRefundedChecks: this.props.storedRequest?.showRefundedChecks ?? false,
    searchRefundCheckStartDate:
      this.props.storedRequest?.refundCheckStartDate ?? undefined,
    searchRefundCheckEndDate: this.props.storedRequest?.refundCheckEndDate ?? undefined,
    gridData: new pagedList().items as ReceivedCheckSummaryResult[],
    page: 1,
    pageCount: 0,
    firstRecordIndex: 0,
    lastRecordIndex: 0,
    totalItems: 0,
    pageSize: 25,
    gridLoading: false,
    receivedChecksTotals: new ReceivedCheckTotalSummary(),
    selectedCheck: undefined as unknown as ReceivedCheckSummaryResult,
    unapplyCheckModalOpen: false,
    checkDetailsModalOpen: false,
    refundedCheckModalOpen: false,
    selectedCheckDetails: undefined as unknown as ReceivedCheckDetailSummary,
    checkDetailModalTitle: '',
    checkDetailIcon: <CreditCard />,
    RefundedCheckDetailIcon: <LocalAtm />,
    isCheckDetailModalLoading: false,
    isUnappliedHistory: false,
    hasUnappliedFunds: false,
    markAsDeletedModalOpen: false,
  }

  public componentDidMount() {}

  private setPaginationOffsetData = () => {
    const { page, totalItems, pageSize } = this.state
    const firstRecordIndex = (page - 1) * pageSize + 1
    const lastRecordIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    this.setState({
      firstRecordIndex: firstRecordIndex,
      lastRecordIndex: lastRecordIndex,
    })
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ page: page + 1 }, () => this.getReceivedChecksData(undefined, true))
  }

  private isSearchGridDataEmpty = () => {
    return this.state.totalItems == 0
  }

  private paginationInfo = () => {
    if (!this.isSearchGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }

  private getAppliedCheckDetail = (checkId: string) => {
    this.setState({ isCheckDetailModalLoading: true })

    getReceivedCheckAppliedFundsDetail(checkId)
      .then((response) => {
        this.setState({ selectedCheckDetails: response })
      })
      .finally(() => {
        this.setState({
          checkDetailsModalOpen: true,
          checkDetailIcon: <CreditCard />,
          isCheckDetailModalLoading: false,
          checkDetailModalTitle: 'APPLIED DETAILS',
          isUnappliedHistory: false,
        })
      })
  }

  private getUnappliedCheckDetail = (checkId: string) => {
    this.setState({ isCheckDetailModalLoading: true })

    getUnappliedReceivedCheckHistory(checkId)
      .then((response) => {
        this.setState({ selectedCheckDetails: response })
      })
      .finally(() => {
        this.setState({
          checkDetailsModalOpen: true,
          checkDetailIcon: <ArchiveOutline />,
          isCheckDetailModalLoading: false,
          checkDetailModalTitle: 'UNAPPLIED DETAILS',
          isUnappliedHistory: true,
        })
      })
  }

  private getRefundedCheckDetail = (checkId: string) => {
    const selectedCheck = this.state.gridData.find((c) => c.checkId === checkId)
    this.setState({
      selectedCheck,
      refundedCheckModalOpen: true,
    })
  }

  private getReceivedChecksData = async (
    setSavedSearch: boolean = false,
    forPageChange: boolean = false
  ) => {
    this.setState({ gridLoading: true })
    const { page, pageSize } = this.state

    const data: ReceivedCheckSearchRequest = {
      checkDateStart: this.state.searchCheckDateStart,
      checkDateEnd: this.state.searchCheckDateEnd,
      checkNumber: this.state.searchCheckNumber,
      payer: this.state.searchPayer,
      onlyShowRefunds: this.state.searchShowRefundOnly,
      hasUnappliedFunds: this.state.searchUnappliedFunds,
      showRefundedChecks: this.state.searchRefundedChecks,
      refundCheckStartDate: this.state.searchRefundCheckStartDate,
      refundCheckEndDate: this.state.searchRefundCheckEndDate,
      episodeOfCareNumberOrLegacyId: this.state.searchEocOrLegacy,
      claimGroupNumber: this.state.searchClaimGroupNumber,
      page: page,
      pageSize: pageSize,
    }
    await searchReceivedChecks(data).then(async (response: any) => {
      if (forPageChange) {
        this.setState({
          gridData: response.items,
          pageCount: response.totalPages,
          totalItems: response.totalItems,
          gridLoading: false,
        })
      } else {
        await getReceivedChecksTotalsSummary(data).then((totals: any) => {
          this.setState(
            {
              gridData: response.items,
              pageCount: response.totalPages,
              totalItems: response.totalItems,
              gridLoading: false,
              receivedChecksTotals: totals,
            },
            () => {
              if (response.totalItems > 0) {
                this.setPaginationOffsetData()
              }
            }
          )

          if (setSavedSearch) {
            this.props.setCheckRequest!(data)
          }
        })
      }
    })
  }

  private search = (event: any) => {
    this.setState({ page: 1, gridLoading: true }, () => {
      this.getReceivedChecksData(true)
    })
    event.preventDefault()
  }

  private clearSearch = () => {
    var select_box = document.getElementById('claimStatus') as HTMLSelectElement
    if (select_box != undefined) {
      select_box.selectedIndex = 0
    }

    var select_box2 = document.getElementById('billoutStatus') as HTMLSelectElement
    if (select_box2 != undefined) {
      select_box2.selectedIndex = 0
    }
    var start = document.getElementById('startDate') as HTMLInputElement
    if (start != undefined) {
      start.value = ''
    }
    var end = document.getElementById('endDate') as HTMLInputElement
    if (end != undefined) {
      end.value = ''
    }

    var end = document.getElementById('refundCheckStartDate') as HTMLInputElement
    if (end != undefined) {
      end.value = ''
    }

    var end = document.getElementById('refundCheckEndDate') as HTMLInputElement
    if (end != undefined) {
      end.value = ''
    }

    var refundCheckDate = document.getElementById('refundCheckDate') as HTMLInputElement
    if (refundCheckDate != undefined) {
      refundCheckDate.value = ''
    }
    this.setState({
      searchCheckDateStart: undefined,
      searchCheckDateEnd: undefined,
      searchPayer: '',
      searchCheckNumber: '',
      searchEocOrLegacy: '',
      searchClaimGroupNumber: undefined,
      gridData: [],
      pageCount: 0,
      totalItems: 0,
      gridLoading: false,
      searchShowRefundOnly: false,
      searchUnappliedFunds: false,
      searchRefundedChecks: false,
      searchRefundCheckStartDate: undefined,
      searchRefundCheckEndDate: undefined,
      page: 1,
    })
  }

  private handleStartDateChange = (date: any) => {
    this.setState({ searchCheckDateStart: date ? moment(date).format('YYYY-MM-DD') : '' })
  }

  private handleRefundCheckStartDateChange = (date: any) => {
    this.setState({
      searchRefundCheckStartDate: date ? moment(date).format('YYYY-MM-DD') : '',
    })
  }

  private handleRefundCheckEndDateChange = (date: any) => {
    this.setState({
      searchRefundCheckEndDate: date ? moment(date).format('YYYY-MM-DD') : '',
    })
  }

  private handleEndDateChange = (date: any) => {
    this.setState({ searchCheckDateEnd: date ? moment(date).format('YYYY-MM-DD') : '' })
  }

  private handlePayerChange = (event: any) => {
    this.setState({ searchPayer: event.target.value })
  }

  private handleCheckNumberChange = (event: any) => {
    this.setState({ searchCheckNumber: event.target.value })
  }

  private handleEocOrLegacyChange = (event: any) => {
    this.setState({ searchEocOrLegacy: event.target.value })
  }

  private handleClaimGroupNumberChange = (event: any) => {
    const value = event.target.value
    this.setState({
      searchClaimGroupNumber: value === '' ? undefined : parseInt(value, 10),
    })
  }

  private getLineItemStyle = (markedDeleted: boolean): React.CSSProperties => {
    if (markedDeleted) {
      return { textDecoration: 'line-through red' }
    } else {
      return { color: 'inherit' }
    }
  }

  private handleShowRefundOnly = (_event: React.ChangeEvent, value: boolean) => {
    this.setState({ searchShowRefundOnly: value })
  }

  private handleUnappliedFunds = (_event: React.ChangeEvent, value: boolean) => {
    this.setState({ searchUnappliedFunds: value })
  }

  private handleRefundedChecks = (_event: React.ChangeEvent, value: boolean) => {
    this.setState({ searchRefundedChecks: value })
  }

  private mapSearchGridData = () => {
    if (this.isSearchGridDataEmpty()) {
      return (
        <TableRow className="gridNoPadding" key={1}>
          <TableCell colSpan={12} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return this.state.gridData.map((record, index) => (
        <TableRow className="gridPadding" key={index}>
          <TableCell
            style={{
              whiteSpace: 'nowrap',
              paddingRight: 0,
              paddingLeft: '20px',
            }}
          >
            {record.refundAmount && record.refundCheckDate && record.refundCheckNumber ? (
              <>
                <Tooltip title="Show Client Refund Details">
                  <span>
                    <LocalAtm
                      style={{
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        this.getRefundedCheckDetail(record.checkId)
                      }}
                    />
                  </span>
                </Tooltip>
                &nbsp;&nbsp;
              </>
            ) : (
              <>
                <ArchiveOutline style={{ visibility: 'hidden' }} />
                &nbsp;&nbsp;
              </>
            )}
            {record.hasHistoricalUnappliedFunds ? (
              <>
                <Tooltip title="Show Unapplied Funds History">
                  <span>
                    <ArchiveOutline
                      style={{
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        this.getUnappliedCheckDetail(record.checkId)
                      }}
                    />
                  </span>
                </Tooltip>
                &nbsp;&nbsp;
              </>
            ) : (
              <>
                <ArchiveOutline style={{ visibility: 'hidden' }} />
                &nbsp;&nbsp;
              </>
            )}
            {record.hasAppliedFunds ? (
              <>
                <Tooltip title="Show Applied Funds Detail">
                  <CreditCard
                    style={{
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      this.getAppliedCheckDetail(record.checkId)
                    }}
                  />
                </Tooltip>
                &nbsp;&nbsp;
              </>
            ) : (
              <>
                <CreditCard style={{ visibility: 'hidden' }} />
                &nbsp;&nbsp;
              </>
            )}
          </TableCell>
          <TableCell sx={this.getLineItemStyle(record.markedDeleted!)}>
            {record.payer}
          </TableCell>
          <TableCell align="right" sx={this.getLineItemStyle(record.markedDeleted!)}>
            {record.checkDate ? formatDate(record.checkDate.toString()) : ''}
          </TableCell>
          <TableCell sx={this.getLineItemStyle(record.markedDeleted!)}>
            {record.checkNumber}
          </TableCell>
          <TableCell align="right" sx={this.getLineItemStyle(record.markedDeleted!)}>
            {formatCurrency(record.checkAmount.toString())}
          </TableCell>
          <TableCell align="right" sx={this.getLineItemStyle(record.markedDeleted!)}>
            {formatCurrency(record.amountApplied.toString())}
          </TableCell>
          <TableCell align="right" sx={this.getLineItemStyle(record.markedDeleted!)}>
            {formatCurrency(record.remainingFunds.toString())}
          </TableCell>
          <TableCell sx={this.getLineItemStyle(record.markedDeleted!)}>
            {record.refund ? <Check color="error" /> : <></>}
          </TableCell>
          <TableCell align="right" sx={this.getLineItemStyle(record.markedDeleted!)}>
            {record.paidClaims}
          </TableCell>
          <TableCell align="right" sx={this.getLineItemStyle(record.markedDeleted!)}>
            {record.paidInvoices}
          </TableCell>
          <TableCell>
            {record.hasAppliedFunds &&
              !record.refundCheckDate &&
              !record.refundAmount &&
              !record.refundCheckNumber && (
                <Tooltip title="Unapply Check">
                  <UndoRounded
                    color={'error'}
                    style={{
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      this.setState({
                        selectedCheck: record,
                        unapplyCheckModalOpen: true,
                      })
                    }}
                  />
                </Tooltip>
              )}
            &nbsp;&nbsp;
            {!record.hasAppliedFunds &&
              !record.markedDeleted &&
              !record.refundCheckDate &&
              !record.refundAmount &&
              !record.refundCheckNumber && (
                <Tooltip title="Mark check as deleted">
                  <Delete
                    style={{ cursor: 'pointer', color: 'red' }}
                    onClick={() => {
                      this.setState({
                        selectedCheck: record,
                        markAsDeletedModalOpen: true,
                      })
                    }}
                  />
                </Tooltip>
              )}
          </TableCell>
          <TableCell
            style={{
              whiteSpace: 'nowrap',
            }}
          >
            {!record.refundCheckNumber &&
            !record.markedDeleted &&
            record.hasUnappliedFunds &&
            !record.refund ? (
              <>
                <Tooltip title="Click to Refund Applied Funds to Client">
                  <span>
                    <LocalAtm
                      color={'error'}
                      style={{
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        this.getRefundedCheckDetail(record.checkId)
                      }}
                    />
                  </span>
                </Tooltip>
                &nbsp;&nbsp;
              </>
            ) : (
              <>
                <ArchiveOutline style={{ visibility: 'hidden' }} />
                &nbsp;&nbsp;
              </>
            )}
          </TableCell>
        </TableRow>
      ))
    }
  }

  public render() {
    const {
      refundedCheckModalOpen,
      unapplyCheckModalOpen,
      checkDetailsModalOpen,
      markAsDeletedModalOpen,
    } = this.state
    return (
      <>
        <Helmet>
          <title>Received Checks</title>
        </Helmet>
        <ResponsiveAppBar title="Received Checks" pageIcon={<Drafts />} />
        <Paper style={{ margin: '20px', height: '100%', paddingBottom: '20px' }}>
          {this.state.selectedCheck && this.state.refundedCheckModalOpen && (
            <RefundedCheckDetailsModal
              open={refundedCheckModalOpen}
              close={() => this.setState({ refundedCheckModalOpen: false })}
              check={this.state.selectedCheck}
              hasRefundedCheckInformation={
                this.state.selectedCheck.refundCheckNumber !== undefined &&
                this.state.selectedCheck.refundCheckDate !== undefined &&
                this.state.selectedCheck.refundAmount !== undefined
              }
              onRefundComplete={() => this.getReceivedChecksData(true)}
            />
          )}
          {this.state.selectedCheck && this.state.unapplyCheckModalOpen && (
            <UnApplyCheckModal
              close={(reload: boolean) => {
                if (reload) {
                  this.getReceivedChecksData(true)
                }
                this.setState({ unapplyCheckModalOpen: false })
              }}
              open={unapplyCheckModalOpen}
              check={this.state.selectedCheck}
            />
          )}
          {this.state.checkDetailsModalOpen && this.state.selectedCheckDetails && (
            <CheckDetailsModal
              close={(reload: boolean) => {
                if (reload) {
                  this.getReceivedChecksData(true)
                }
                this.setState({ checkDetailsModalOpen: false })
              }}
              open={checkDetailsModalOpen}
              checkDetail={this.state.selectedCheckDetails}
              title={this.state.checkDetailModalTitle}
              dialogIcon={this.state.checkDetailIcon}
              isUnappliedHistory={this.state.isUnappliedHistory}
            />
          )}
          {this.state.selectedCheck && this.state.markAsDeletedModalOpen && (
            <MarkAsDeletedModal
              close={(reload: boolean) => {
                if (reload) {
                  this.getReceivedChecksData(true)
                }
                this.setState({ markAsDeletedModalOpen: false })
              }}
              open={markAsDeletedModalOpen}
              check={this.state.selectedCheck}
            />
          )}
          {(this.state.gridLoading || this.state.isCheckDetailModalLoading) && (
            <div
              style={{
                position: 'fixed',
                display: 'flex',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                cursor: 'pointer',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: '2',
              }}
            >
              <CircularProgress size={100} />
            </div>
          )}
          <form onSubmit={() => {}}>
            <Grid container style={{ padding: '10px', marginLeft: '10px' }} xs={12}>
              <Typography
                color="primary"
                component="h6"
                variant="h6"
                gutterBottom
                style={{ paddingBottom: '10px' }}
              >
                SEARCH CRITERIA
              </Typography>
              <Grid container direction="row" spacing={2} style={{ width: '100%' }}>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <TextField
                      value={this.state.searchPayer}
                      onChange={this.handlePayerChange}
                      variant="outlined"
                      label="Payer"
                      name="searchPayer"
                      InputLabelProps={{ shrink: true }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <FormControl fullWidth>
                    <TextField
                      value={this.state.searchCheckNumber}
                      onChange={this.handleCheckNumberChange}
                      label="Check Number"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ fullWidth: true }}
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={1}>
                  <FormControl fullWidth>
                    <TextField
                      value={this.state.searchEocOrLegacy}
                      onChange={this.handleEocOrLegacyChange}
                      label="EOC/Legacy"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ fullWidth: true }}
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={1}>
                  <FormControl fullWidth>
                    <TextField
                      value={this.state.searchClaimGroupNumber ?? ''}
                      onChange={this.handleClaimGroupNumberChange}
                      label="Claim Group #"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ fullWidth: true }}
                      variant="outlined"
                      type="number"
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid
                container
                xs={12}
                spacing={1}
                direction="row"
                item
                justifyContent="flex-start"
                alignItems="center"
                style={{ paddingTop: '15px' }}
              >
                <Grid item xs={2}>
                  <DatePicker
                    value={
                      this.state.searchCheckDateStart
                        ? moment(this.state.searchCheckDateStart)
                        : null
                    }
                    onChange={this.handleStartDateChange}
                    label="Provider Check Start Date"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        InputLabelProps: { shrink: true },
                      },
                      openPickerIcon: { fontSize: 'small' },
                    }}
                  />
                </Grid>
                <Grid
                  item
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <strong> - </strong>
                </Grid>
                <Grid item xs={2}>
                  <DatePicker
                    value={
                      this.state.searchCheckDateEnd
                        ? moment(this.state.searchCheckDateEnd)
                        : null
                    }
                    onChange={this.handleEndDateChange}
                    label="Provider Check End Date"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        InputLabelProps: { shrink: true },
                      },
                      openPickerIcon: { fontSize: 'small' },
                    }}
                  />
                </Grid>
                <Grid item xs={2}>
                  <DatePicker
                    value={
                      this.state.searchRefundCheckStartDate
                        ? moment(this.state.searchRefundCheckStartDate)
                        : null
                    }
                    onChange={this.handleRefundCheckStartDateChange}
                    label="Client Refund Check Start Date"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        InputLabelProps: { shrink: true },
                      },
                      openPickerIcon: { fontSize: 'small' },
                    }}
                  />
                </Grid>
                <Grid
                  item
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <strong> - </strong>
                </Grid>
                <Grid item xs={2}>
                  <DatePicker
                    value={
                      this.state.searchRefundCheckEndDate
                        ? moment(this.state.searchRefundCheckEndDate)
                        : null
                    }
                    onChange={this.handleRefundCheckEndDateChange}
                    label="Client Refund Check End Date"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: 'outlined',
                        InputLabelProps: { shrink: true },
                      },
                      openPickerIcon: { fontSize: 'small' },
                    }}
                  />
                </Grid>
              </Grid>
              <Grid
                container
                xs={12}
                spacing={1}
                direction="row"
                item
                justifyContent="flex-start"
                alignItems="center"
                style={{ paddingTop: '15px' }}
              >
                <Grid item style={{ paddingRight: '16px' }}>
                  <FormControl>
                    <FormControlLabel
                      label="Show Unapplied Funds"
                      labelPlacement="end"
                      control={
                        <Checkbox
                          checked={this.state.searchUnappliedFunds}
                          onChange={this.handleUnappliedFunds}
                          inputProps={{
                            id: 'show-unapplied-funds-filter',
                          }}
                          value="searchUnappliedFunds"
                          name="searchUnappliedFunds"
                        />
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid item style={{ paddingRight: '16px' }}>
                  <FormControl>
                    <FormControlLabel
                      label="Only Show Checks with Client Refunds"
                      labelPlacement="end"
                      control={
                        <Checkbox
                          checked={this.state.searchRefundedChecks}
                          onChange={this.handleRefundedChecks}
                          inputProps={{
                            id: 'show-refunded-checks-filter',
                          }}
                          value="searchRefundedChecks"
                          name="searchRefundedChecks"
                        />
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={1}>
                  <FormControl>
                    <FormControlLabel
                      label="Only Show Provider Refund Checks"
                      labelPlacement="end"
                      control={
                        <Checkbox
                          checked={this.state.searchShowRefundOnly}
                          onChange={this.handleShowRefundOnly}
                          inputProps={{
                            id: 'show-refund-filter',
                          }}
                          value="searchShowRefundOnly"
                          name="searchShowRefundOnly"
                        />
                      }
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Grid
                container
                direction="row"
                spacing={2}
                style={{
                  marginTop: '10px',
                  paddingTop: '10px',
                  width: '100%',
                }}
              >
                <Grid item>
                  <Button
                    type="submit"
                    onClick={this.search}
                    color="primary"
                    size="medium"
                    variant="contained"
                  >
                    Search
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    color="secondary"
                    onClick={this.clearSearch}
                    size="medium"
                    variant="contained"
                  >
                    Clear Search
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              container
              direction="row"
              style={{ paddingTop: '20px', paddingLeft: '20px', paddingBottom: '10px' }}
              xs={12}
            >
              <Grid item xs={12}>
                <Typography noWrap variant="h5">
                  Check Count:&nbsp;
                  <strong>
                    {this.isSearchGridDataEmpty()
                      ? 'N/A'
                      : this.state.receivedChecksTotals.checkCount}
                  </strong>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography noWrap variant="h5">
                  Total Funds:&nbsp;
                  <strong>
                    {this.isSearchGridDataEmpty() ? (
                      'N/A'
                    ) : (
                      <CurrencyFormat
                        value={this.state.receivedChecksTotals.totalCheckAmount}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={'$'}
                        fixedDecimalScale={true}
                        decimalScale={2}
                      />
                    )}
                  </strong>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography noWrap variant="h5">
                  Total Applied Funds:&nbsp;
                  <strong>
                    {this.isSearchGridDataEmpty() ? (
                      'N/A'
                    ) : (
                      <CurrencyFormat
                        value={this.state.receivedChecksTotals.totalCheckAppliedFunds}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={'$'}
                        fixedDecimalScale={true}
                        decimalScale={2}
                      />
                    )}
                  </strong>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography noWrap variant="h5">
                  Total Client Refunds:&nbsp;
                  <strong>
                    {this.isSearchGridDataEmpty() ? (
                      'N/A'
                    ) : (
                      <CurrencyFormat
                        value={this.state.receivedChecksTotals.totalRefundedFunds}
                        displayType={'text'}
                        thousandSeparator={true}
                        prefix={'$'}
                        fixedDecimalScale={true}
                        decimalScale={2}
                      />
                    )}
                  </strong>
                </Typography>
              </Grid>
            </Grid>
          </form>
          <Grid
            item
            container
            direction="row"
            justifyContent="space-between"
            alignContent="center"
            alignItems="flex-start"
            wrap="wrap"
          >
            <Grid item xs={12}>
              <Table aria-label="simple table" style={{ padding: '20px' }}>
                <TableHead>
                  <TableRow className="gridPadding">
                    <TableCell style={{ paddingLeft: '20px' }}></TableCell>
                    <TableCell>Payer</TableCell>
                    <TableCell align="right">Check Date</TableCell>
                    <TableCell>Check Number</TableCell>
                    <TableCell align="right">Check Amount</TableCell>
                    <TableCell align="right">Amount Applied</TableCell>
                    <TableCell align="right">Remaining Funds</TableCell>
                    <TableCell>Provider Refund</TableCell>
                    <TableCell align="right"># of Claims</TableCell>
                    <TableCell align="right"># of Invoices</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody style={{ width: '10%' }}>{this.mapSearchGridData()}</TableBody>
              </Table>
              <div className="pagination-row">
                {this.paginationInfo()}
                <ReactPaginate
                  previousLabel={'<'}
                  nextLabel={'>'}
                  onPageChange={this.handlePageChange}
                  forcePage={this.state.page - 1}
                  pageCount={this.state.pageCount}
                  containerClassName={'pagination'}
                  activeClassName={'active'}
                  //@ts-ignore
                  renderOnZeroPageCount={null}
                />
              </div>
            </Grid>
          </Grid>
        </Paper>
      </>
    )
  }
}

const InjectedReceivedChecks = inject<
  IStores,
  IReceivedCheckProps,
  Partial<IReceivedCheckProps>,
  any
>((stores: IStores) => ({
  setCheckRequest: stores.global.setCheckRequest,
  storedRequest: stores.global.checkRequest,
}))(ReceivedChecks)

export default InjectedReceivedChecks
